/**
 * Shared types for the grading engine.
 *
 * The engine is pure and surface-agnostic: it reads a realized score and a stream of
 * performance events and returns judgments. Nothing here knows which screen called it.
 * Score positions are in beats (quarter notes from the start of the score); performance
 * times are in milliseconds on the same timeline. A {@link Clock} converts between them.
 */

export type Hand = 'left' | 'right';

export const HANDS: readonly Hand[] = ['left', 'right'];

/** The note dimensions a judgment may grade. Pedal is a separate stream, not a note dimension. */
export type Dimension = 'pitch' | 'onset' | 'offset' | 'duration' | 'velocity' | 'finger';

export const DIMENSIONS: readonly Dimension[] = [
  'pitch',
  'onset',
  'offset',
  'duration',
  'velocity',
  'finger',
];

/** The three timing dimensions. At most two of them may be graded at once. */
export const TIMING_DIMENSIONS: readonly Dimension[] = ['onset', 'offset', 'duration'];

/** Dimensions that need a running clock: they cannot be graded under order matching. */
export const CLOCK_DIMENSIONS: readonly Dimension[] = ['onset', 'offset'];

/** One note of the realized score (ornaments already expanded, arrangement already chosen). */
export interface ScoreNote {
  readonly id: string;
  /** MIDI note number. */
  readonly pitch: number;
  /** Notated onset, in beats from the start of the score. */
  readonly onsetBeat: number;
  /** Notated release, in beats. Must be after the onset. */
  readonly offsetBeat: number;
  readonly hand: Hand;
  /** Matching region this note belongs to. Notes without one use the default region. */
  readonly region?: string;
  /** Overrides onset-slice membership; by default notes sharing a notated onset form a slice. */
  readonly onsetSlice?: string;
  /** Overrides release-slice membership; by default notes sharing a notated offset form one. */
  readonly releaseSlice?: string;
  /** True when the note belongs to a rolled (arpeggiated) chord. */
  readonly rolled?: boolean;
  /** Target velocity (0-127) where the score asks for a dynamic. */
  readonly targetVelocity?: number;
  /** Notated finger (1-5). */
  readonly finger?: number;
  /** Bounded-scope group labels, e.g. `{ phrase: 'p3', measure: '12' }`. */
  readonly groups?: Readonly<Record<string, string>>;
}

/** A notated sustain-pedal span, for presets that grade the pedal stream. */
export interface ScorePedalMark {
  readonly id: string;
  readonly downBeat: number;
  readonly upBeat: number;
}

/**
 * Where a score came from, in the surface's own terms (e.g. an exercise's package key, level
 * and tier). The engine never reads it; a take record carries it with the score, so a stored
 * take names the content it was realized from.
 */
export type ScoreSource = Readonly<Record<string, string | number>>;

export interface RealizedScore {
  readonly notes: readonly ScoreNote[];
  readonly pedalMarks?: readonly ScorePedalMark[];
  /** The score's own tempo marking, used as the last fallback reference tempo. */
  readonly notatedTempoBpm?: number;
  readonly source?: ScoreSource;
}

export type PedalKind = 'sustain' | 'sostenuto' | 'soft';

export interface PressEvent {
  readonly kind: 'press';
  readonly timeMs: number;
  readonly pitch: number;
  readonly velocity: number;
  /** Detected finger (1-5), when the input can track fingers. */
  readonly finger?: number;
}

export interface ReleaseEvent {
  readonly kind: 'release';
  readonly timeMs: number;
  readonly pitch: number;
}

export interface PedalEvent {
  readonly kind: 'pedal';
  readonly timeMs: number;
  readonly pedal: PedalKind;
  /** Controller value 0-127. Sustain is down at 64 and above. */
  readonly value: number;
}

/** Marks a reset point for "since last reset" scopes (e.g. a loop restarting). */
export interface ScopeResetEvent {
  readonly kind: 'scope-reset';
  readonly timeMs: number;
}

export type PerformanceEvent = PressEvent | ReleaseEvent | PedalEvent | ScopeResetEvent;

/** Thrown when a configuration breaks a validity rule of the model. */
export class GradingConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GradingConfigError';
    // Keeps `instanceof` working when compiled down to ES5.
    Object.setPrototypeOf(this, GradingConfigError.prototype);
  }
}
